import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { modalBackdropProps, modalBackdropSx } from "../../theme/modalBackdrop";
import { useCSRFToken } from "../../hooks/useCSRFToken";
import { useAuth } from "../../contexts/AuthContext";
import { patchNotificationCategoryPreferences } from "../../services/notificationCategoryPreferencesApi";
import {
  deleteMyTrainingGeoAlertsState,
  getMyTrainingGeoAlerts,
  getTrainingAlertCounties,
  getTrainingAlertStates,
  isTrainingGeoAlertsUnauthorizedError,
  putMyTrainingGeoAlerts,
  type TeGeoCounty,
  type TeGeoState,
  type TrainingGeoAlertStateBlock,
} from "../../services/trainingEventGeoAlertsApi";
import TrainingGeoAlertAuthWall from "./TrainingGeoAlertAuthWall";
import { recordSubscribed } from "../../../shared/conversion/trainingAlertsNudgeFrequency";
import TrainingGeoAlertGuestForm from "./TrainingGeoAlertGuestForm";
import {
  saveTrainingGeoAlertGuestIntent,
  type TrainingGeoAlertGuestIntent,
} from "../../../shared/trainingGeoAlertGuestIntent";

/** Guest (email-first) signup replaces the auth wall as the primary path when enabled. */
const GUEST_ALERTS_ENABLED =
  import.meta.env.VITE_TRAINING_EVENT_GEO_ALERTS_GUEST_ENABLED === "true";

export type TrainingEventGeoAlertsModalProps = {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
  initialGeoStateId?: number | null;
  initialCountyIds?: number[];
  /** Campaign label stored with guest subscriptions created from this surface. */
  guestSource?: string;
};

export default function TrainingEventGeoAlertsModal({
  open,
  onClose,
  onSaved,
  initialGeoStateId = null,
  initialCountyIds = [],
  guestSource = "alerts_modal",
}: TrainingEventGeoAlertsModalProps) {
  const { isAuthenticated, isInitializing } = useAuth();
  const { token: csrfToken } = useCSRFToken();
  const [step, setStep] = useState<1 | 2>(1);
  const [states, setStates] = useState<TeGeoState[]>([]);
  const [counties, setCounties] = useState<TeGeoCounty[]>([]);
  const [stateLoading, setStateLoading] = useState(false);
  const [countyLoading, setCountyLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [authRequired, setAuthRequired] = useState(false);
  const [selectedState, setSelectedState] = useState<TeGeoState | null>(null);
  const [selectedCounties, setSelectedCounties] = useState<TeGeoCounty[]>([]);
  const [optIn, setOptIn] = useState(false);
  const [emailEnabledMaster, setEmailEnabledMaster] = useState(true);
  const [emailMasterLoading, setEmailMasterLoading] = useState(false);
  const [existingBlocks, setExistingBlocks] = useState<
    TrainingGeoAlertStateBlock[]
  >([]);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [pickNewState, setPickNewState] = useState(false);
  const [deletingGeoStateId, setDeletingGeoStateId] = useState<number | null>(
    null,
  );

  const pendingHydrationRef = useRef<{
    stateId: number;
    countyIds: number[];
  } | null>(null);
  const didInitialRouteRef = useRef(false);

  const initialCountyIdsKey = useMemo(
    () => initialCountyIds.join(","),
    [initialCountyIds],
  );

  /** Email-first path: no account; state-wide by default with optional county narrowing. */
  const guestMode = GUEST_ALERTS_ENABLED && !isAuthenticated && !isInitializing;

  const loadStates = useCallback(async () => {
    setStateLoading(true);
    setError(null);
    try {
      const r = await getTrainingAlertStates();
      setStates(r.data || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load states");
      setStates([]);
    } finally {
      setStateLoading(false);
    }
  }, []);

  const refreshSubscription = useCallback(async () => {
    if (!isAuthenticated) {
      setEmailEnabledMaster(true);
      setExistingBlocks([]);
      return null;
    }
    const r = await getMyTrainingGeoAlerts();
    setEmailEnabledMaster(r.data.email_enabled ?? true);
    setExistingBlocks(r.data.states ?? []);
    return r;
  }, [isAuthenticated]);

  const buildGuestIntent = useCallback((): TrainingGeoAlertGuestIntent => {
    return {
      geoStateId: selectedState?.id ?? initialGeoStateId ?? null,
      countyIds:
        selectedCounties.length > 0
          ? selectedCounties.map((c) => c.id)
          : initialCountyIds,
      source: "event_detail",
    };
  }, [
    selectedState,
    selectedCounties,
    initialGeoStateId,
    initialCountyIds,
  ]);

  useEffect(() => {
    if (!open) {
      didInitialRouteRef.current = false;
      return;
    }
    didInitialRouteRef.current = false;
    setError(null);
    setAuthRequired(false);
    setPickNewState(false);
    pendingHydrationRef.current = null;
    void (async () => {
      setSubscriptionLoading(true);
      try {
        await loadStates();
        if (isInitializing) {
          return;
        }
        if (!isAuthenticated) {
          setEmailEnabledMaster(true);
          setExistingBlocks([]);
          // Guest signup is a complete path on its own — no blocking auth wall.
          setAuthRequired(!GUEST_ALERTS_ENABLED);
          return;
        }
        await refreshSubscription();
      } catch (e) {
        if (isTrainingGeoAlertsUnauthorizedError(e)) {
          setEmailEnabledMaster(true);
          setExistingBlocks([]);
          setAuthRequired(!GUEST_ALERTS_ENABLED);
        } else {
          setEmailEnabledMaster(true);
          setExistingBlocks([]);
        }
      } finally {
        setSubscriptionLoading(false);
      }
    })();
  }, [
    open,
    loadStates,
    refreshSubscription,
    isAuthenticated,
    isInitializing,
  ]);

  useEffect(() => {
    if (guestMode) return;
    if (!open || subscriptionLoading || stateLoading || states.length === 0)
      return;
    if (didInitialRouteRef.current) return;
    didInitialRouteRef.current = true;

    if (initialGeoStateId != null) {
      const st = states.find((s) => s.id === initialGeoStateId) ?? null;
      if (st) {
        const block = existingBlocks.find(
          (b) => b.geo_state_id === initialGeoStateId,
        );
        const countyIdsFromProps = initialCountyIdsKey
          ? initialCountyIdsKey
              .split(",")
              .map((s) => Number(s))
              .filter((n) => Number.isFinite(n) && n > 0)
          : [];
        const countyIds =
          countyIdsFromProps.length > 0
            ? countyIdsFromProps
            : (block?.counties.map((c) => c.id) ?? []);
        pendingHydrationRef.current = { stateId: initialGeoStateId, countyIds };
        setSelectedState(st);
        setStep(2);
        setOptIn(true);
        return;
      }
    }

    setStep(1);
    setSelectedState(null);
    setOptIn(false);
  }, [
    open,
    subscriptionLoading,
    stateLoading,
    states,
    existingBlocks,
    initialGeoStateId,
    initialCountyIdsKey,
    guestMode,
  ]);

  const handleEmailMasterChange = async (enabled: boolean) => {
    if (!isAuthenticated) {
      setAuthRequired(true);
      return;
    }
    setEmailMasterLoading(true);
    setError(null);
    try {
      await patchNotificationCategoryPreferences(
        { training_event_geo_alerts: { email: enabled } },
        csrfToken ?? undefined,
      );
      setEmailEnabledMaster(enabled);
    } catch (e) {
      if (isTrainingGeoAlertsUnauthorizedError(e)) {
        setAuthRequired(true);
        return;
      }
      setError(
        e instanceof Error ? e.message : "Failed to update email preference",
      );
    } finally {
      setEmailMasterLoading(false);
    }
  };

  useEffect(() => {
    // In guest mode the guest form owns its own county lookup and selection state.
    if (!open || !selectedState || guestMode) {
      setCounties((prev) => (prev.length === 0 ? prev : []));
      return;
    }
    let cancelled = false;
    (async () => {
      setCountyLoading(true);
      setError(null);
      try {
        const r = await getTrainingAlertCounties(selectedState.id);
        if (cancelled) return;
        const list = r.data || [];
        setCounties(list);

        const ph = pendingHydrationRef.current;
        if (ph && ph.stateId === selectedState.id) {
          const picked = list.filter((c) => ph.countyIds.includes(c.id));
          setSelectedCounties(picked);
          pendingHydrationRef.current = null;
        } else {
          setSelectedCounties([]);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Failed to load counties");
          setCounties([]);
        }
      } finally {
        if (!cancelled) setCountyLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, selectedState, guestMode]);

  const canSave = useMemo(() => {
    if (!selectedState || !optIn || saveLoading) return false;
    return selectedCounties.length > 0;
  }, [selectedState, optIn, saveLoading, selectedCounties.length]);

  const handleSave = async () => {
    if (!selectedState || !canSave) return;
    if (!isAuthenticated) {
      saveTrainingGeoAlertGuestIntent(buildGuestIntent());
      setAuthRequired(true);
      return;
    }
    setSaveLoading(true);
    setError(null);
    try {
      await putMyTrainingGeoAlerts({
        geo_state_id: selectedState.id,
        county_ids: selectedCounties.map((c) => c.id),
      });
      await refreshSubscription();
      try {
        recordSubscribed(
          typeof localStorage !== "undefined" ? localStorage : null,
        );
      } catch {
        // ignore
      }
      onSaved?.();
      onClose();
    } catch (e) {
      if (isTrainingGeoAlertsUnauthorizedError(e)) {
        saveTrainingGeoAlertGuestIntent(buildGuestIntent());
        setAuthRequired(true);
        return;
      }
      setError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleEditBlock = (block: TrainingGeoAlertStateBlock) => {
    const st = states.find((s) => s.id === block.geo_state_id) ?? null;
    if (!st) {
      setError("Could not resolve state for this alert. Try again.");
      return;
    }
    pendingHydrationRef.current = {
      stateId: block.geo_state_id,
      countyIds: block.counties.map((c) => c.id),
    };
    setSelectedState(st);
    setStep(2);
    setOptIn(true);
    setPickNewState(false);
  };

  const handleRemoveBlock = async (geoStateId: number) => {
    if (!isAuthenticated) {
      setAuthRequired(true);
      return;
    }
    setDeletingGeoStateId(geoStateId);
    setError(null);
    try {
      await deleteMyTrainingGeoAlertsState(geoStateId);
      await refreshSubscription();
    } catch (e) {
      if (isTrainingGeoAlertsUnauthorizedError(e)) {
        setAuthRequired(true);
        return;
      }
      setError(
        e instanceof Error
          ? e.message
          : "Failed to remove alerts for this state",
      );
    } finally {
      setDeletingGeoStateId(null);
    }
  };

  const goBackToStep1 = () => {
    setStep(1);
    setSelectedState(null);
    setCounties([]);
    setSelectedCounties([]);
    setOptIn(false);
    pendingHydrationRef.current = null;
    setPickNewState(false);
  };

  const showStatePicker =
    step === 1 &&
    (existingBlocks.length === 0 || pickNewState) &&
    !stateLoading &&
    !subscriptionLoading;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      disableEnforceFocus
      BackdropProps={modalBackdropProps}
      sx={{
        zIndex: 2100,
        "& .MuiBackdrop-root": { ...modalBackdropSx },
      }}
    >
      <DialogTitle>Training alerts by location</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {guestMode
              ? "Pick a U.S. state, optionally narrow to counties, and enter your email. We'll send a confirmation link, then email you when new training is posted in those areas."
              : "Choose a U.S. state, then one or more counties. We'll email you when a new training event is posted in those counties."}
          </Typography>

          {guestMode ? (
            <TrainingGeoAlertGuestForm
              states={states}
              statesLoading={stateLoading}
              initialGeoStateId={initialGeoStateId}
              initialCountyIds={initialCountyIds}
              source={guestSource}
              onSubscribed={() => {
                onSaved?.();
              }}
              upgradeSlot={
                <TrainingGeoAlertAuthWall
                  compact
                  intent={buildGuestIntent()}
                  title="Want to manage alerts in one place?"
                  body="Create a free account to add more states, edit counties, and manage every alert from your dashboard."
                />
              }
            />
          ) : null}

          {authRequired ? (
            <TrainingGeoAlertAuthWall intent={buildGuestIntent()} />
          ) : null}

          {isAuthenticated ? (
            <>
              <FormControlLabel
                control={
                  <Switch
                    checked={emailEnabledMaster}
                    disabled={emailMasterLoading}
                    onChange={(e) => void handleEmailMasterChange(e.target.checked)}
                    inputProps={{
                      "aria-label":
                        "Email me for training event alerts in my chosen areas",
                    }}
                  />
                }
                label="Email me for training event alerts in my chosen areas"
              />
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mt: -1 }}
              >
                Turning this off stops emails but keeps your county selections. You
                can also manage this under Dashboard → Settings → Notifications.
              </Typography>
            </>
          ) : null}

          {error ? (
            <Alert severity="error" role="alert">
              {error}
            </Alert>
          ) : null}

          {guestMode ? null : subscriptionLoading || stateLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={28} aria-label="Loading alert settings" />
            </Box>
          ) : step === 1 ? (
            <Box>
              {isAuthenticated && existingBlocks.length > 0 ? (
                <Stack spacing={1.5} sx={{ mb: showStatePicker ? 1 : 0 }}>
                  <Typography variant="subtitle2">
                    Your current alerts
                  </Typography>
                  {existingBlocks.map((block) => (
                    <Box
                      key={block.geo_state_id}
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: 1,
                        py: 1,
                        borderBottom: 1,
                        borderColor: "divider",
                      }}
                    >
                      <Typography variant="body2" sx={{ flex: "1 1 200px" }}>
                        {block.summary}
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleEditBlock(block)}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        disabled={deletingGeoStateId === block.geo_state_id}
                        onClick={() =>
                          void handleRemoveBlock(block.geo_state_id)
                        }
                      >
                        {deletingGeoStateId === block.geo_state_id
                          ? "Removing…"
                          : "Remove"}
                      </Button>
                    </Box>
                  ))}
                  <Button variant="text" onClick={() => setPickNewState(true)}>
                    Add another state
                  </Button>
                </Stack>
              ) : null}

              {showStatePicker ? (
                <Autocomplete
                  disablePortal
                  options={states}
                  getOptionLabel={(o) =>
                    [o.name, o.iso2].filter(Boolean).join(" — ") || String(o.id)
                  }
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  value={selectedState}
                  onChange={(_, v) => {
                    pendingHydrationRef.current = null;
                    setSelectedState(v);
                    setOptIn(false);
                    if (v) setStep(2);
                  }}
                  blurOnSelect
                  clearOnBlur={false}
                  ListboxProps={{ sx: { maxHeight: 280 } }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="State"
                      margin="normal"
                      required
                      placeholder="Type to search"
                      inputProps={{
                        ...params.inputProps,
                        "aria-label": "Select state",
                      }}
                    />
                  )}
                />
              ) : null}
            </Box>
          ) : (
            <>
              <Button size="small" onClick={goBackToStep1}>
                {existingBlocks.length > 0 ? "Back" : "Change state"}
              </Button>
              <Typography variant="subtitle2">
                {selectedState
                  ? [selectedState.name, selectedState.iso2]
                      .filter(Boolean)
                      .join(" — ")
                  : ""}
              </Typography>

              {countyLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <CircularProgress size={28} aria-label="Loading counties" />
                </Box>
              ) : (
                <Autocomplete
                  disablePortal
                  multiple
                  options={counties}
                  getOptionLabel={(c) => c.name || String(c.id)}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  value={selectedCounties}
                  onChange={(_, v) => setSelectedCounties(v)}
                  blurOnSelect
                  clearOnBlur={false}
                  ListboxProps={{ sx: { maxHeight: 280 } }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Counties"
                      margin="normal"
                      placeholder="Type to search counties"
                      helperText="Select one or more counties"
                      inputProps={{
                        ...params.inputProps,
                        "aria-label": "Select counties",
                      }}
                    />
                  )}
                />
              )}

              <FormControlLabel
                control={
                  <Checkbox
                    checked={optIn}
                    onChange={(e) => setOptIn(e.target.checked)}
                    inputProps={{ "aria-label": "Opt in to email alerts" }}
                  />
                }
                label="I want email alerts for new training in the areas I select. I can turn this off anytime from alert settings or unsubscribe links in emails."
              />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">
          {guestMode ? "Close" : "Cancel"}
        </Button>
        {!guestMode && step === 2 ? (
          <Button
            variant="contained"
            disabled={!canSave}
            onClick={() => void handleSave()}
            startIcon={
              saveLoading ? (
                <CircularProgress size={16} color="inherit" aria-hidden />
              ) : undefined
            }
          >
            Save alerts
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}

export function formatTrainingGeoAlertSummaries(
  states: TrainingGeoAlertStateBlock[],
): string {
  if (!states.length) return "No alerts configured";
  return states.map((s) => s.summary).join(" · ");
}
